import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeft, Send, Check, Wifi } from "lucide-react";

// Add NetworkInformation interface
interface NetworkInformation extends EventTarget {
  effectiveType: string;
  downlink: number;
}

// Extend Navigator interface
declare global {
  interface Navigator {
    connection?: NetworkInformation;
  }
}

interface ContactSupportFormProps {
  onBack: () => void;
  completedSteps?: number[];
}

interface SystemInfo {
  browser: string;
  browserVersion: string;
  operatingSystem: string;
  screenResolution: string;
  language: string;
  connection: string;
  downloadSpeed: string;
  isSpeedTestComplete: boolean;
}

const ContactSupportForm = ({
  onBack,
  completedSteps = [],
}: ContactSupportFormProps) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSpeedTesting, setIsSpeedTesting] = useState(false);
  const [systemInfo, setSystemInfo] = useState<SystemInfo>({
    browser: "",
    browserVersion: "",
    operatingSystem: "",
    screenResolution: "",
    language: "",
    connection: "",
    downloadSpeed: "Testing...",
    isSpeedTestComplete: false,
  });

  useEffect(() => {
    // Get system information
    const userAgent = navigator.userAgent;

    // Get browser and version
    let browser = "Unknown";
    let browserVersion = "";

    if (userAgent.indexOf("Chrome") > -1) {
      browser = "Chrome";
      browserVersion = userAgent.match(/Chrome\/([0-9.]+)/)?.[1] || "";
    } else if (userAgent.indexOf("Safari") > -1) {
      browser = "Safari";
      browserVersion = userAgent.match(/Version\/([0-9.]+)/)?.[1] || "";
    } else if (userAgent.indexOf("Firefox") > -1) {
      browser = "Firefox";
      browserVersion = userAgent.match(/Firefox\/([0-9.]+)/)?.[1] || "";
    } else if (userAgent.indexOf("Edge") > -1) {
      browser = "Edge";
      browserVersion = userAgent.match(/Edge\/([0-9.]+)/)?.[1] || "";
    }

    // Get OS
    let operatingSystem = "Unknown";
    if (userAgent.indexOf("Win") !== -1) operatingSystem = "Windows";
    else if (userAgent.indexOf("Mac") !== -1) operatingSystem = "MacOS";
    else if (userAgent.indexOf("Linux") !== -1) operatingSystem = "Linux";
    else if (userAgent.indexOf("Android") !== -1) operatingSystem = "Android";
    else if (userAgent.indexOf("iOS") !== -1) operatingSystem = "iOS";

    // Get screen resolution
    const screenResolution = `${window.screen.width}x${window.screen.height}`;

    // Get language
    const language = navigator.language || "en-US";

    // Get connection info with fallback
    let connection = "unknown";
    try {
      if (navigator.connection) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const conn = navigator.connection as any;
        connection = `${conn.effectiveType || "unknown"} (${conn.downlink || "unknown"
          } Mbps)`;
      }
    } catch (error) {
      console.warn("Network Information API not supported");
    }

    setSystemInfo({
      browser,
      browserVersion,
      operatingSystem,
      screenResolution,
      language,
      connection,
      downloadSpeed: "Testing...",
      isSpeedTestComplete: false,
    });
  }, []);

  // Parse string to array if it is a string
  const completedStepsArray = (() => {
    if (typeof completedSteps === "string") {
      try {
        const parsed = JSON.parse(completedSteps);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return Array.isArray(completedSteps) ? completedSteps : [];
  })().filter((step): step is number => {
    return typeof step === "number" && step >= 0 && step < 5;
  });

  const completedStepsText =
    completedStepsArray.length > 0
      ? completedStepsArray
        .map((stepIndex: number) => {
          const stepTitles = [
            "Tried a different device or browser",
            "Updated browser",
            "Tried incognito/private mode",
            "Reset browser settings",
            "Whitelisted Nicole the Math Lady domains",
          ];
          return `- ${stepTitles[stepIndex]}`;
        })
        .join("\n")
      : "- No troubleshooting steps completed yet";

  // Set initial message when component mounts
  useEffect(() => {
    setMessage(
      `I'm having trouble playing videos on Nicole the Math Lady.\n\n` +
      `Here's what I've tried:\n${completedStepsText}\n\n` +
      `The issue I'm experiencing:\n- [Please describe what happens when you try to play videos]`
    );
  }, [completedStepsText]);

  const runSpeedTest = async () => {
    if (isSpeedTesting) return;

    setIsSpeedTesting(true);
    try {
      // Using a test file from Cloudflare's CDN
      const testFileUrl =
        "https://cdnjs.cloudflare.com/ajax/libs/jquery/3.7.1/jquery.min.js";
      const startTime = performance.now();

      const response = await fetch(testFileUrl, { cache: "no-store" });
      const endTime = performance.now();

      if (!response.ok) throw new Error("Speed test failed");

      const fileSize = parseInt(response.headers.get("content-length") || "0");
      const durationInSeconds = (endTime - startTime) / 1000;
      const speedMbps = (fileSize * 8) / (1024 * 1024 * durationInSeconds);

      setSystemInfo((prev) => ({
        ...prev,
        downloadSpeed: `${speedMbps.toFixed(2)} Mbps`,
        isSpeedTestComplete: true,
      }));
    } catch (error) {
      setSystemInfo((prev) => ({
        ...prev,
        downloadSpeed: "Test failed",
        isSpeedTestComplete: true,
      }));
    } finally {
      setIsSpeedTesting(false);
    }
  };

  // Run initial speed test and set up periodic testing
  useEffect(() => {
    // Run initial test
    runSpeedTest();

    // Set up periodic testing every 30 seconds
    const intervalId = setInterval(runSpeedTest, 30000);

    // Cleanup interval on component unmount
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name || !email || !message) {
      toast.error("Please fill out all fields");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch("/api/report-video-issue", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
          platform: systemInfo.operatingSystem,
          language: systemInfo.language,
          location: "unknown", // This would need to be obtained from a geolocation service
          connection: systemInfo.connection,
          screen: systemInfo.screenResolution,
          status: "pending",
          email: email,
          notes: message,
          error: "Video playback issue",
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to send support request");
      }

      toast.success("Support request sent successfully", {
        description: "We'll get back to you as soon as possible.",
      });

      // Clear the form
      setName("");
      setEmail("");
      setMessage("");
    } catch (error) {
      toast.error("Failed to send support request", {
        description:
          error instanceof Error ? error.message : "Please try again later",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h2 className="text-2xl font-bold text-primary tracking-tight">
          Contact Support
        </h2>
        <p className="text-muted-foreground">
          Our team will help you get your videos working properly.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <label htmlFor="name" className="text-sm font-medium">
            Your Name
          </label>
          <Input
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter your name"
            required
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="email" className="text-sm font-medium">
            Email Address
          </label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email address"
            required
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="message" className="text-sm font-medium">
            Message
          </label>
          <Textarea
            id="message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Describe the issue you're experiencing"
            rows={6}
            required
          />
        </div>

        <div className="p-3 bg-muted rounded-md border text-xs text-muted-foreground space-y-2">
          <p className="font-medium">
            System Information (will be included with your message)
          </p>
          <table className="w-full">
            <tbody>
              <tr>
                <td className="pr-4">Browser:</td>
                <td>
                  {systemInfo.browser} {systemInfo.browserVersion}
                </td>
              </tr>
              <tr>
                <td className="pr-4">Operating System:</td>
                <td>{systemInfo.operatingSystem}</td>
              </tr>
              <tr>
                <td className="pr-4">Screen Resolution:</td>
                <td>{systemInfo.screenResolution}</td>
              </tr>
              <tr>
                <td className="pr-4">Language:</td>
                <td>{systemInfo.language}</td>
              </tr>
              <tr>
                <td className="pr-4">Connection:</td>
                <td>{systemInfo.connection}</td>
              </tr>
              <tr>
                <td className="pr-4">Download Speed:</td>
                <td className="flex items-center gap-2">
                  {systemInfo.downloadSpeed}
                  {isSpeedTesting && (
                    <span className="text-xs text-muted-foreground">
                      (testing...)
                    </span>
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 justify-between pt-2">
          <Button
            type="button"
            variant="ghost"
            className="border border-muted"
            onClick={onBack}
            disabled={isSubmitting}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Troubleshooting
          </Button>

          <Button
            type="submit"
            className="bg-primary hover:bg-primary/90"
            disabled={isSubmitting}
          >
            <Send className="mr-2 h-4 w-4" />
            {isSubmitting ? "Sending..." : "Send Support Request"}
          </Button>
        </div>
      </form>

      {/* <div className="text-center text-sm text-muted-foreground">
        <p className="flex items-center justify-center gap-1">
          <Check className="h-4 w-4 text-success" />
          We'll respond within one business day
        </p>
      </div> */}
    </div>
  );
};

export default ContactSupportForm;
